import React, { useState, useCallback } from "react"
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ExportButton from "./ExportButton"
import { SharedFilterValues } from "./SharedFilters"
import * as hashAuditService from "../../services/hashAuditService"
import { AuditRow, AuditType } from "../../services/hashAuditService"
import { formatCentsWithSymbol } from "../../utils/formatCurrency"

interface AuditsTabProps {
    companyId: string
}

function todayISO(): string {
    return new Date().toISOString().slice(0, 10)
}

function oneMonthAgoISO(): string {
    const d = new Date()
    d.setMonth(d.getMonth() - 1)
    return d.toISOString().slice(0, 10)
}

function truncateHash(hash: string | null): string {
    if (!hash) return "—"
    return hash.length > 8 ? `${hash.slice(0, 8)}…` : hash
}

const AUDIT_TYPES: { value: AuditType; label: string }[] = [
    { value: "payments", label: "Audit des paiements" },
    { value: "transactions", label: "Audit des transactions" },
    { value: "mouvements", label: "Audit des mouvements de caisse" },
]

const AuditsTab: React.FC<AuditsTabProps> = ({ companyId }) => {
    const [auditType, setAuditType] = useState<AuditType>("payments")
    const [dateFrom, setDateFrom] = useState(oneMonthAgoISO())
    const [dateTo, setDateTo] = useState(todayISO())
    const [rows, setRows] = useState<AuditRow[]>([])
    const [total, setTotal] = useState(0)
    const [loaded, setLoaded] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    const dateError = React.useMemo(() => {
        if (!dateFrom || !dateTo) return null
        const from = new Date(dateFrom)
        const to = new Date(dateTo)
        if (to < from) return "La date de fin doit être >= à la date de début"
        const diffDays = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
        if (diffDays > 366) return "La plage de dates ne peut pas dépasser 1 an"
        return null
    }, [dateFrom, dateTo])

    const fetchPage = useCallback(
        async (nextPage: number, nextPageSize: number) => {
            if (!companyId || dateError) return
            setLoading(true)
            setError(null)
            try {
                const filters = {
                    dateFrom,
                    dateTo,
                    page: nextPage,
                    pageSize: nextPageSize,
                }
                let data
                if (auditType === "payments") {
                    data = await hashAuditService.getPaymentsAudit(companyId, filters)
                } else if (auditType === "transactions") {
                    data = await hashAuditService.getTransactionsAudit(companyId, filters)
                } else {
                    data = await hashAuditService.getMovementsAudit(companyId, filters)
                }
                setRows(data.rows)
                setTotal(data.total)
                setPage(data.page)
                setRowsPerPage(data.pageSize)
                setLoaded(true)
            } catch (err: any) {
                setError(err?.message || "Erreur lors du chargement de l'audit")
            } finally {
                setLoading(false)
            }
        },
        [companyId, auditType, dateFrom, dateTo, dateError]
    )

    const handleLoad = useCallback(async () => {
        await fetchPage(0, rowsPerPage)
    }, [fetchPage, rowsPerPage])

    const exportFilters: SharedFilterValues = {
        outletId: "",
        registerId: "",
        dateFrom,
        dateTo,
    }

    const reportTypeForExport =
        auditType === "payments"
            ? "audit_payments"
            : auditType === "transactions"
              ? "audit_transactions"
              : "audit_mouvements"

    const errCount = rows.filter((r) => r.status === "err").length

    return (
        <Box>
            {/* Filters panel */}
            <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel>Type d'audit</InputLabel>
                    <Select
                        value={auditType}
                        label="Type d'audit"
                        onChange={(e) => {
                            setAuditType(e.target.value as AuditType)
                            setLoaded(false)
                            setRows([])
                        }}
                    >
                        {AUDIT_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Du"
                    type="date"
                    size="small"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!!dateError}
                />
                <TextField
                    label="Au"
                    type="date"
                    size="small"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!!dateError}
                />

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SearchIcon />}
                    onClick={handleLoad}
                    disabled={loading || !!dateError}
                    sx={{ alignSelf: "center" }}
                >
                    Charger
                </Button>

                {loaded && (
                    <Box sx={{ ml: "auto", alignSelf: "center" }}>
                        <ExportButton
                            companyId={companyId}
                            reportType={reportTypeForExport}
                            filters={exportFilters}
                        />
                    </Box>
                )}
            </Paper>

            {dateError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {dateError}
                </Alert>
            )}

            {loading && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && !loading && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loaded && !loading && (
                <>
                    {/* Summary bar */}
                    <Paper sx={{ p: 1.5, mb: 2, display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
                        <Typography variant="body2">
                            <strong>Légende :</strong> Hash256 (HMAC-SHA256)
                        </Typography>
                        <Typography variant="body2">
                            <strong>Total :</strong> {total}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Page :</strong> {rows.length - errCount} OK
                        </Typography>
                        <Typography variant="body2" color={errCount > 0 ? "error" : "inherit"}>
                            <strong>ERR (page) :</strong> {errCount}
                        </Typography>
                    </Paper>

                    {total === 0 ? (
                        <Paper sx={{ p: 3, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                                Aucun enregistrement pour cette période.
                            </Typography>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        {auditType !== "mouvements" && (
                                            <TableCell align="right">Montant</TableCell>
                                        )}
                                        {auditType === "mouvements" && (
                                            <>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Direction</TableCell>
                                                <TableCell>Caisse</TableCell>
                                                <TableCell>Session</TableCell>
                                                <TableCell>Caissier</TableCell>
                                            </>
                                        )}
                                        <TableCell>Hash prev (Hash256)</TableCell>
                                        <TableCell>Hash (Hash256)</TableCell>
                                        <TableCell>Statut</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                {row.date
                                                    ? new Date(row.date).toLocaleString("fr-FR")
                                                    : "—"}
                                            </TableCell>
                                            {auditType !== "mouvements" && (
                                                <TableCell align="right">
                                                    {row.amount_minor != null && row.currency
                                                        ? formatCentsWithSymbol(
                                                              row.amount_minor,
                                                              row.currency
                                                          )
                                                        : row.amount_minor != null
                                                          ? `${row.amount_minor}`
                                                          : "—"}
                                                </TableCell>
                                            )}
                                            {auditType === "mouvements" && (
                                                <>
                                                    <TableCell>{row.movement_type ?? "—"}</TableCell>
                                                    <TableCell>{row.direction ?? "—"}</TableCell>
                                                    <TableCell>{row.register_id ?? "—"}</TableCell>
                                                    <TableCell>{row.session_id ?? "—"}</TableCell>
                                                    <TableCell>{row.cashier ?? "—"}</TableCell>
                                                </>
                                            )}
                                            <TableCell>
                                                <Tooltip title={row.previous_hash ?? ""}>
                                                    <span
                                                        style={{
                                                            fontFamily: "monospace",
                                                            fontSize: "0.75rem",
                                                        }}
                                                    >
                                                        {truncateHash(row.previous_hash)}
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={row.hash ?? ""}>
                                                    <span
                                                        style={{
                                                            fontFamily: "monospace",
                                                            fontSize: "0.75rem",
                                                        }}
                                                    >
                                                        {truncateHash(row.hash)}
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {row.status === "ok" ? (
                                                    <Chip
                                                        label="OK"
                                                        color="success"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Chip
                                                        label={
                                                            row.ticket_ref
                                                                ? `ERR - ${row.ticket_ref}`
                                                                : "ERR"
                                                        }
                                                        color="error"
                                                        size="small"
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(_e, p) => {
                                    void fetchPage(p, rowsPerPage)
                                }}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    const next = parseInt(e.target.value, 10)
                                    void fetchPage(0, next)
                                }}
                                rowsPerPageOptions={[25, 50, 100]}
                                labelRowsPerPage="Lignes par page :"
                            />
                        </TableContainer>
                    )}
                </>
            )}
        </Box>
    )
}

export default AuditsTab
